import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from './SubmitButton';
import { LoginResult, loginWithNewPassword } from './api';
import { useFormSubmit } from './useFormSubmit';
import { usePasswordPair } from './usePasswordPair';

interface Props {
  session: string;
  email: string;
  onMfaRequired: (challenge: Extract<LoginResult, { status: 'MFA_REQUIRED' }> & { email: string }) => void;
  /**
   * The account must enrol an authenticator. This is the usual path for a brand-new user
   * on an MFA-required pool: set the temporary password, then enrol.
   */
  onMfaSetupRequired: (state: { session: string; email: string }) => void;
  onSelectMfaType: (state: Extract<LoginResult, { status: 'SELECT_MFA_TYPE' }> & { email: string }) => void;
  onBack: () => void;
  /** Language deliberately picked on this page, if any. */
  chosenLang?: string;
}

export const NewPasswordForm = ({
  session,
  email,
  onMfaRequired,
  onMfaSetupRequired,
  onSelectMfaType,
  onBack,
  chosenLang,
}: Props) => {
  const { t } = useTranslation();
  const { loading, error, setError, runSubmit } = useFormSubmit();
  const { newPassword, mismatch, fields, isIncomplete } = usePasswordPair(
    {
      newPassword: t('login.newPassword.label'),
      confirm: t('login.newPassword.confirm'),
      mismatch: t('login.newPassword.mismatch'),
    },
    true,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    await runSubmit(async () => {
      const result = await loginWithNewPassword({ session, email, newPassword, lang: chosenLang });
      if (result.status === 'OK') {
        globalThis.location.href = '/';
      } else if (result.status === 'MFA_REQUIRED') {
        onMfaRequired({ ...result, email });
      } else if (result.status === 'MFA_SETUP_REQUIRED') {
        onMfaSetupRequired({ session: result.session, email });
      } else if (result.status === 'SELECT_MFA_TYPE') {
        onSelectMfaType({ ...result, email });
      }
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.newPassword.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {fields}

        <SubmitButton loading={loading} disabled={isIncomplete}>
          {t('login.newPassword.submit')}
        </SubmitButton>

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
